import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';

echarts.use([TooltipComponent, GridComponent, BarChart, CanvasRenderer]);

interface AbuseIndexChartProps {
  overallScore: number;
  title: string;
  description: string;
}

const AbuseIndexChart: React.FC<AbuseIndexChartProps> = ({ overallScore, title, description }) => {
  const { getThemeColor } = useAppContext();

  // Ensure we have valid colors with fallbacks
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const options = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        return `<strong>Overall Abuse Index:</strong> ${param.value.toFixed(1)} / 100`;
      }
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      splitLine: {
        show: true,
        lineStyle: { color: borderColor, type: 'dashed' }
      }
    },
    yAxis: {
      type: 'category',
      data: [''],
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { show: false }
    },
    series: [
      {
        type: 'bar',
        data: [
          {
            value: overallScore,
            itemStyle: {
              color: primaryColor,
              borderRadius: [0, 5, 5, 0]
            }
          }
        ],
        barWidth: '30%',
        label: {
          show: true,
          position: 'right',
          formatter: `${overallScore.toFixed(1)} / 100`,
          color: lightTextEmphasis,
          fontSize: 16,
          fontWeight: 'bold'
        }
      },
      {
        type: 'bar',
        data: [
          {
            value: 100 - overallScore,
            itemStyle: {
              color: borderColor + '40',
              borderRadius: [5, 0, 0, 5]
            }
          }
        ],
        barWidth: '30%',
        barGap: '-100%',
        silent: true
      }
    ],
    grid: { left: '5%', right: '15%', bottom: '10%', top: '10%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">{title}</h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '200px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default AbuseIndexChart;
